export type CampaignOffer = {
  id: string;
  playerMessage: string;
  application: 'first_deposit' | 'every_deposit';
  bonusType: 'fixed' | 'percentage';
  bonusValue: number;
  minDeposit: number;
  maxBonusPerDeposit: number;
  endsAt: string | null;
  /** True after this player has taken the one first-deposit slot. */
  used?: boolean;
};

export type CampaignOfferPayload = {
  offers: CampaignOffer[];
  offerKey: string;
  popupDismissed: boolean;
};

export function formatOfferMoney(amount: number) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

function plainNumber(amount: number) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** Short line for the header strip. No dollar bonus until the player picks an amount. */
export function offerHeadline(offer: Pick<CampaignOffer, 'bonusType' | 'bonusValue' | 'maxBonusPerDeposit'>) {
  if (offer.bonusType === 'fixed') return `${formatOfferMoney(offer.bonusValue)} extra`;
  return `${plainNumber(offer.bonusValue)}% up to ${formatOfferMoney(offer.maxBonusPerDeposit)}`;
}

export function offerStripSummary(offers: Array<Pick<CampaignOffer, 'bonusType' | 'bonusValue' | 'maxBonusPerDeposit'>>) {
  return offers.map(offerHeadline).join(' · ');
}

/** Rule shown before a deposit amount exists. */
export function offerRule(offer: Pick<CampaignOffer, 'bonusType' | 'bonusValue' | 'minDeposit' | 'maxBonusPerDeposit'>) {
  const min = formatOfferMoney(offer.minDeposit);
  if (offer.bonusType === 'fixed') {
    return `${formatOfferMoney(offer.bonusValue)} extra · deposits from ${min}`;
  }
  return `${plainNumber(offer.bonusValue)}% extra, up to ${formatOfferMoney(offer.maxBonusPerDeposit)} · deposits from ${min}`;
}

/** What the player gets, including whether it is the first deposit or every deposit. */
export function offerWhat(offer: Pick<CampaignOffer, 'application' | 'bonusType' | 'bonusValue' | 'minDeposit' | 'maxBonusPerDeposit'>) {
  const min = formatOfferMoney(offer.minDeposit);
  const when = offer.application === 'first_deposit' ? 'your first deposit in this offer' : 'every deposit';
  if (offer.bonusType === 'fixed') {
    return `${formatOfferMoney(offer.bonusValue)} extra on ${when} of ${min} or more`;
  }
  return `${plainNumber(offer.bonusValue)}% extra, up to ${formatOfferMoney(offer.maxBonusPerDeposit)}, on ${when} of ${min} or more`;
}

/** When the offer stops. The campaign window ends at this instant. */
export function offerUntil(endsAt: string | null) {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(end);
  return `Ends ${formatted}`;
}
